/* eslint-disable no-console */
import * as Microsoft from '@microsoft/document-collaboration-sdk'
import { Button, Spinning } from '@zoom/react-ui'
import { IconChevronLeftSmall } from '@zoom/icons-react'
import { FC, useEffect, useRef, useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAppDispatch, useAppSelector } from '../hooks'
import { clearFile, FileMS, selectFile, setFile } from '../app/file'
import { appApi, useLazyIsFileSharableQuery } from '../app/appApiQueries'
import { setIsNavigatingDuringCollab, setPage } from '../app/root'
import { zoomSdkApi } from '../app/zoomSdkQueries'
import { CollabFileMS, selectCollabFile } from '../app/collabFile'
import { useOnOpenLink } from '../hooks/useOnOpenLink'

const MicrosoftFileViewer: FC<{ collab: boolean }> = ({ collab }) => {
  const selectedFile = useAppSelector(selectFile) as FileMS
  const collabFile = useAppSelector(selectCollabFile) as CollabFileMS
  let id = selectedFile?.id,
    siteUrl = selectedFile?.siteUrl,
    resourceUrl = selectedFile?.resourceUrl,
    driveId = selectedFile?.driveId
  if (collab) {
    id = collabFile?.fileId
    siteUrl = collabFile?.siteUrl
    resourceUrl = collabFile?.resourceUrl
    driveId = collabFile?.driveId
  }
  const containerRef = useRef<HTMLDivElement | null>(null)
  const launched = useRef<boolean>(false) // using a ref instead of useState to not isFileSharableTrigger re-render (renders 2 iframes)
  const { auth } = appApi.endpoints.login.useQueryState('Microsoft', {
    selectFromResult: (result) => ({ auth: result?.data?.data }),
  })
  const tokenRef = useRef(auth!.extra?.resource?.[resourceUrl])
  // TODO: test if resourceUrl is ever unknown, create a function to select any token
  const [isFirstMount, setIsFirstMount] = useState(true)
  const { runningContext } = zoomSdkApi.endpoints.config.useQueryState(
    'config',
    {
      selectFromResult: (result) => ({
        runningContext: result?.data?.runningContext,
      }),
    }
  )
  const [isFileSharableTrigger] = useLazyIsFileSharableQuery()
  // ============================= Start V3 =============================
  // Add code back to enable Native Collaboration
  /*
  const [isFileSharableTrigger, { data }] = useLazyIsFileSharableQuery()
  */
  // ============================= End V3 =============================

  useOnOpenLink(runningContext)

  const dispatch = useAppDispatch()

  const launchDocuments = useCallback(
    (container: HTMLDivElement) => {
      Microsoft.initializeDocument({
        hostName: 'MDCPPPrototype',
        hostClientType: 'web',
      })

      Microsoft.launch({
        documentInfo: {
          documentIdentifier: {
            siteUrl: siteUrl!,
            sourceDoc: id,
          },
        },
        sessionInfo: {
          hostCorrelationId: uuidv4(),
        },
        container,
        fetchAccessToken: async (resourceUrl: string, claim?: string[]) => {
          const scopes = []
          if (claim && claim.length > 0) {
            claim.forEach((val) => {
              scopes.push(`${resourceUrl}/${val}`)
            })
          } else {
            scopes.push(resourceUrl)
          }
          return tokenRef.current!
        },
      })
        .then((value: Microsoft.BootInfo) => {
          setIsFirstMount(false)
          console.log(
            'Launch API finished {0}, {1}',
            value.isBootSuccess,
            value.errorInfo
          )
        })
        .catch(() => {
          dispatch(setPage('error'))
        })
    },
    [siteUrl, id, dispatch]
  )

  useEffect(() => {
    if (
      id &&
      driveId &&
      runningContext &&
      runningContext !== 'inMainClient' &&
      runningContext !== 'inCollaborate'
    ) {
      isFileSharableTrigger({
        drive: 'Microsoft',
        fileId: id,
        fileDriveId: driveId,
      })
    }
  }, [driveId, id, isFileSharableTrigger, runningContext])

  useEffect(() => {
    if (auth && resourceUrl) {
      tokenRef.current = auth!.extra?.resource?.[resourceUrl]
    }
  }, [auth, resourceUrl])

  useEffect(() => {
    const ref = containerRef.current
    if (
      !launched.current &&
      ref &&
      !ref.querySelector('#SharepointDocAspxFrame') &&
      id &&
      auth?.accessToken &&
      siteUrl
    ) {
      launched.current = true
      launchDocuments(ref)
    }
  }, [id, launchDocuments, launched, siteUrl, auth?.accessToken])

  // ============================= Start V3 =============================
  // Add code back to enable Native Collaboration
  /*
  useEffect(() => {
    if (
      runningContext &&
      runningContext !== 'inMainClient' &&
      runningContext !== 'inCollaborate'
    ) {
      if (data?.shareable) {
        zoomSdk
          .zmSetCollaborateButtonState({ setState: 'enabled' })
          .catch(() => {
            // swallow
          })
      } else {
        zoomSdk
          .zmSetCollaborateButtonState({ setState: 'disabled' })
          .catch(() => {
            // swallow
          })
      }
    }
  }, [runningContext, data?.shareable])
  */
  // ============================= End V3 =============================

  // set the selected file on first collab render so the receiver can stay on this page once they leave collab
  useEffect(() => {
    if (collab && collabFile?.fileId) {
      dispatch(
        setFile({
          name: collabFile.fileName,
          url: collabFile.fileUrl,
          type: collabFile.fileType,
          id: collabFile.fileId,
          driveId: collabFile.driveId,
          siteUrl: collabFile.siteUrl,
          resourceUrl: collabFile.resourceUrl,
        })
      )
    }
  }, [collab, collabFile, dispatch])

  const returnHome = useCallback(() => {
    dispatch(clearFile())
    if (collab) {
      dispatch(setIsNavigatingDuringCollab(true))
    }
  }, [collab, dispatch])

  return (
    <Spinning
      spinning={isFirstMount}
      size='large'
      label=''
    >
      <div className='flex h-[36px] items-center bg-[#F5F5F5] px-[16px]'>
        <Button
          size='small'
          type='secondary'
          startIcon={<IconChevronLeftSmall aria-hidden='true' />}
          onClick={returnHome}
        >
          Back to files
        </Button>
      </div>
      <div
        ref={containerRef}
        style={{
          height: 'calc(100vh - 36px)',
          position: 'relative',
        }}
      />
    </Spinning>
  )
}

export default MicrosoftFileViewer
